import { DateTime } from "luxon";
import type { MRT_ColumnDef, MRT_RowData } from "mantine-react-table-open";
import { DATE_DISPLAY_FORMAT } from "../../../utils/dates/dates.constants";
import type { FilterPill } from "../../components/MRTActiveFiltersRow/FilterPillsGroup";

type MRTFilterVariant = NonNullable<
  MRT_ColumnDef<MRT_RowData>["filterVariant"]
>;

interface GetFilterChipsOptions {
  columnId: string;
  filterValue: unknown;
  filterVariant?: MRTFilterVariant;
  header: string;
  getOptionLabel?: (value: unknown) => string | undefined;
}

const RANGE_FILTER_VARIANTS: Array<MRTFilterVariant> = [
  "date-range",
  "range",
  "range-slider"
];

/**
 * Converts a single MRT column filter into the chips displayed in the active filters row.
 * Multi-value filters produce one chip per selected value, range filters a single chip.
 */
export function getFilterChips({
  columnId,
  filterValue,
  filterVariant,
  header,
  getOptionLabel
}: GetFilterChipsOptions): Array<FilterPill> {
  const formatValue = (value: unknown) =>
    getOptionLabel?.(value) ?? formatFilterValue(value, filterVariant);

  if (filterVariant && RANGE_FILTER_VARIANTS.includes(filterVariant)) {
    return getRangeFilterChips({
      columnId,
      filterValue,
      filterVariant,
      formatValue,
      header
    });
  }

  if (Array.isArray(filterValue)) {
    return filterValue.flatMap((entry, index) => {
      if (!isFilterValue(entry)) return [];

      const remainingValues = filterValue.filter(
        (_, entryIndex) => entryIndex !== index
      );

      return [
        {
          columnId,
          key: `${columnId}-${index}`,
          label: `${header}: ${formatValue(entry)}`,
          nextFilterValue:
            remainingValues.length > 0 ? remainingValues : undefined
        }
      ];
    });
  }

  if (!isFilterValue(filterValue)) return [];

  return [
    {
      columnId,
      key: columnId,
      label: `${header}: ${formatValue(filterValue)}`,
      // MRT's handleClear feeds null into Mantine Select when value becomes undefined
      nextFilterValue: filterVariant === "select" ? "" : undefined
    }
  ];
}

interface GetRangeFilterChipsOptions {
  columnId: string;
  filterValue: unknown;
  filterVariant: MRTFilterVariant;
  formatValue: (value: unknown) => string;
  header: string;
}

function getRangeFilterChips({
  columnId,
  filterValue,
  filterVariant,
  formatValue,
  header
}: GetRangeFilterChipsOptions): Array<FilterPill> {
  const [minValue, maxValue] = Array.isArray(filterValue) ? filterValue : [];
  const hasMinValue = isFilterValue(minValue);
  const hasMaxValue = isFilterValue(maxValue);

  if (!hasMinValue && !hasMaxValue) return [];

  const rangeLabel =
    hasMinValue && hasMaxValue
      ? `${formatValue(minValue)} – ${formatValue(maxValue)}`
      : hasMinValue
        ? `ab ${formatValue(minValue)}`
        : `bis ${formatValue(maxValue)}`;

  return [
    {
      columnId,
      key: columnId,
      label: `${header}: ${rangeLabel}`,
      // MRT_FilterRangeSlider handles undefined correctly; MRT_FilterTextInput doesn't
      nextFilterValue: filterVariant === "range-slider" ? undefined : ["", ""]
    }
  ];
}

function formatFilterValue(
  value: unknown,
  filterVariant?: MRTFilterVariant
): string {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat(DATE_DISPLAY_FORMAT);
  }

  if (
    typeof value === "string" &&
    (filterVariant === "date" || filterVariant === "date-range")
  ) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return DateTime.fromJSDate(date).toFormat(DATE_DISPLAY_FORMAT);
    }
  }

  if (DateTime.isDateTime(value)) {
    return value.toFormat(DATE_DISPLAY_FORMAT);
  }

  if (typeof value === "boolean") {
    return value ? "Ja" : "Nein";
  }

  // MRT stores checkbox filter values as the strings "true" and "false"
  if (filterVariant === "checkbox" && (value === "true" || value === "false")) {
    return value === "true" ? "Ja" : "Nein";
  }

  return String(value);
}

function isFilterValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}
